import { GetServerDataReturn, HeadProps, PageProps, graphql } from 'gatsby'
import React, { useMemo } from 'react'
import { cn } from '../../Utils/component'
import { getMediaUrl } from '../../Utils/medias'
import { getSlugFromPermalink } from '../../Utils/tool'
import { BaseHtmlRendering } from '../../components/base/BaseHtmlRendering'
import { AppHead, Layout } from '../../components/common'
import { TeaserProps } from '../../components/offers/teaser'
import { ToolHeader } from '../../components/tools/Toolheader'
import { graphqlToolFirmMapper } from '../../components/tools/mapper'
import { ToolTabs } from '../../components/tools/tabs/ToolTabs'
import { ModalWithMedias } from '../../components/ui/medias/ModalWithMedia'
import { FetchOneToolResponse, fetchOneTool } from '../../services/tools/fetchOneTool.service'
import { Media } from '../../types/Medias'

interface ToolPageProps {
  slug: string
  name: string
  headerImage: string | null
  logo: string | null
  media: Media | null
  description: string | null
}

const ToolPage: React.FC<PageProps<Queries.ToolQuery, FetchOneToolResponse>> = ({ data, location, serverData = {
  tool: {},
  firms: [],
  offers: [],
} }) => {
  const { tool = {}, firms = [], offers = [] } = serverData as FetchOneToolResponse

  const teaserFirms = useMemo(() => firms.map((firm) => graphqlToolFirmMapper(firm)), [firms])
  const mappedOffers: TeaserProps[] = useMemo(() => offers.map((offer) => ({
    city: offer.office?.city || undefined,
    companyName: offer.office?.firm?.name || '',
    contractType: offer.contractType || undefined,
    headerImage: offer.office?.firm?.header?.url || undefined,
    logo: offer.office?.firm?.logo?.url || undefined,
    salary: offer.payRange || undefined,
    startDate: offer.startDate || '',
    slug: offer.slug || '',
    title: offer.title || '',
    url: `/cabinets/${offer.office?.firm?.slug}/offres/${offer.slug}`,
  })), [offers])
  const youTubeApiKey = useMemo(() => data.site?.siteMetadata?.youTubeApiKey || undefined, [data.site?.siteMetadata?.youTubeApiKey])

  const mappedTool: ToolPageProps = useMemo(() => {
    if (!tool) {
      return {} as ToolPageProps
    }

    return {
      slug: tool.name.toLowerCase(),
      name: tool.name,
      headerImage: getMediaUrl(tool.header),
      logo: getMediaUrl(tool.logo),
      media: tool.media || null,
      description: tool.description || null,
    }
  }, [tool])

  return (
    <Layout
      apiToken={data.site?.siteMetadata?.frontStrapiToken || undefined}
      apiUrl={data.site?.siteMetadata?.siteStrapiUrl || undefined}
      location={location}
    >
      <div className="h-full bg-very-light-blue">
        <ToolHeader
          name={mappedTool.name}
          logo={mappedTool.logo}
          header={mappedTool.headerImage}
        />
        <ToolTabs
          location={location}
          tool={tool}
          offers={mappedOffers}
          firms={teaserFirms}
        />
        <div
          className={cn(
            'grid grid-flow-row grid-cols-1 gap-4 mb-20 auto-rows-max mt-6',
            mappedTool.media && mappedTool.description
              ? 'lg:grid-cols-2'
              : 'lg:grid-cols-1',
            !mappedTool.media && !mappedTool.description ? 'hidden' : '',
            mappedTool.media && !mappedTool.description
              ? 'lg:grid-cols-1'
              : '',
            mappedTool.description && !mappedTool.media
              ? 'lg:grid-cols-1'
              : '',
          )}
        >
          {mappedTool.media && (
            <ModalWithMedias
              id={mappedTool.media?.id}
              media={
                mappedTool.media?.media}
              video={mappedTool.media?.video}
              name={mappedTool.name}
              youTubeApiKey={youTubeApiKey}
            />
          )}
          {mappedTool.description && (
            <div className="container mx-auto prose text-night">
              <BaseHtmlRendering html={mappedTool.description} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

export default ToolPage

export async function getServerData({
  params,
}: {
  params: { slug: string }
}): GetServerDataReturn<FetchOneToolResponse> {
  try {
    const backEndUrl = process.env.CORE_STRAPI_URL
    const token = process.env.CORE_STRAPI_READ_TOKEN

    if (!backEndUrl) {
      throw new Error(`Strapi url is not defined GroupPage`)
    }

    if (!token) {
      throw new Error(`Strapi token is not defined GroupPage`)
    }

    const { slug } = params

    if (!slug) {
      throw new Error(`Slug is not defined OfferPage`)
    }

    const {
      tool,
      firms,
      offers,
    } = await fetchOneTool({
      strapiUrl: backEndUrl,
      strapiToken: token,
      slug: slug.toLowerCase(),
    })

    return {
      status: tool ? 200 : 404,
      props: {
        tool,
        firms,
        offers,
      },
    }
  } catch (error) {
    console.error(error, 'error')
    return {
      status: 500,
      headers: {},
      props: {
        tool: null,
        firms: [],
        offers: [],
      },
    }
  }
}

export const Head: React.FC<HeadProps<Queries.ToolQuery, FetchOneToolResponse>> = ({ data, serverData }) => {
  const { tool } = serverData as FetchOneToolResponse

  if (!tool) {
    return null
  }
  const slug = getSlugFromPermalink(tool.permalink) || tool.slug

  const linkCanonical = `${data.site?.siteMetadata?.siteUrl}/outils/${slug}`
  const metaImage =
    tool.header?.url || '/lamacompta-sharing-default.jpg'
  const title = `Les cabinets d'expertise comptable qui utilisent ${tool.name} sur Lamacompta`
  return (
    <AppHead
      title={title}
      metaDescription={`Rejoignez un cabinet qui utilise ${tool.name}, sur Lamacompta.`}
      metaTitle={title}
      linkCanonical={linkCanonical}
      metaImage={metaImage}
    />
  )
}

export const query = graphql`
  query Tool {
    site {
      siteMetadata {
        siteUrl
        frontStrapiToken
        siteStrapiUrl
        youTubeApiKey
      }
    }
  }
`
